/**
 * 🏠 Landing — Iter 215 (2026-06-30)
 *
 * Rebuilt per the SA's "I need a better homepage" directive. The new
 * flow is photo-led + data-led, not graphic-led:
 *
 *    Header
 *    ↓
 *    Hero — full-bleed human photo + dark left overlay + embedded
 *      stats strip docked at the bottom (Iter 216)
 *    ↓
 *    CareerDestinations — 4-col career grid (Hospitality, Logistics,
 *      Skilled Trades, Healthcare) with candid editorial photos
 *    ↓
 *    NonprofitsBreakdown — 3 stacked phase cards (Triage,
 *      Sanctuary, Career)
 *    ↓
 *    EmployerBenefits — 2x3 partner-facing tools grid
 *      (replaces the bottom-of-page jargon ticker)
 *    ↓
 *    PartnersBand — partner logo strip (existing, kept)
 *    ↓
 *    InquiryForm — existing two-tone lead form
 *    ↓
 *    Footer
 *
 * Iter 215 retires (from the public landing only — components still
 * exist for the route system to find if any deep-link references):
 *   • CommitmentBanner.jsx (master-narrative composite — replaced by
 *     the new Hero photo + headline).
 *   • EcosystemTicker.jsx (bottom jargon banner — promoted into a
 *     proper EmployerBenefits section).
 *   • EcosystemCards.jsx (multi-step interactive lead surface — its
 *     lead-capture role is served by the InquiryForm already on the
 *     page; the interactive ecosystem is now told via the Career
 *     Destinations + Nonprofits Breakdown).
 *   • UnifiedJourney.jsx (timeline — story now lives in the
 *     Nonprofits Breakdown's three-card spine).
 *   • ShowcaseCards.jsx (legacy showcase — narrative absorbed).
 */
import { useEffect } from "react";
import Header from "../components/Header";
import Hero from "../components/Hero";
import WelcomeVideoSection from "../components/WelcomeVideoSection";
import CareerDestinations from "../components/CareerDestinations";
import NonprofitsBreakdown from "../components/NonprofitsBreakdown";
import EmployerBenefits from "../components/EmployerBenefits";
import PartnersBand from "../components/PartnersBand";
import InquiryForm from "../components/InquiryForm";
import Footer from "../components/Footer";
// 🎠 PATCH-309 · Auto-scrolling supporters carousel band.
import SupportersCarousel from "../components/SupportersCarousel";
import ImpactTicker from "../components/ImpactTicker";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { ImpactHomepageCTA } from "./PublicImpact";
import { RoadmapPreview } from "./PublicRoadmap";
import { ArrowRight, Briefcase, Home as HomeIcon, Columns3 } from "lucide-react";

export default function Landing() {
  useDocumentMeta({
    title:       "Serving the Last, the Least and the Lost: HIS Mission, HIS Mandate | The Exchange Alliance",
    description: "The Exchange Alliance serves the Last, the Least and the Lost — coordinating regional non-profit partnerships and employer networks through our Personal Advancement and Career Training (PACT) to move people from the streets to stable housing and careers.",
    keywords:    "Personal Advancement and Career Training, PACT, non-profit alliance network, transitional workforce housing coordination, consent-based care coordination, the exchange alliance, serving the last the least and the lost",
    canonical:   "https://theexchangealliance.com/",
  });
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) { window.scrollTo(0, 0); return; }
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <main data-testid="landing-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      {/* 🔎 SEO semantic spine — one <h1> per page + three named <h2>
          anchors mapping to the three primary public surfaces.
          Visually hidden so Googlebot reads them without disrupting
          the new brand layout. */}
      <h1 className="sr-only">The Exchange Alliance — Serving the Last, the Least and the Lost: HIS Mission, HIS Mandate</h1>

      <Header />

      {/* 🌅 Hero — full-bleed editorial human-hero photograph with
          left-aligned dark gradient overlay carrying the headline + CTAs.
          Iter 216: the three "by the numbers" metrics now ride a
          translucent stats strip docked to the bottom of the hero so
          they sit inside the initial viewport with zero scroll. */}
      <Hero />
      <WelcomeVideoSection />

      {/* 📊 Homepage Impact Ticker — live "$X raised · N residents active"
          pulse rail that rides right under the hero so donors feel the
          mission's momentum the moment the page paints. */}
      <ImpactTicker />

      {/* 📊 Wave 1 · Public Impact Bridge — compact CTA band directing
          donors / grantmakers to the dedicated /impact page. */}
      <ImpactHomepageCTA />

      {/* 👷 Wave 3 + 📝 Wave 2 · Dual public-entry CTA — one door for
          employers (labor calculator), one door for prospective
          residents (intake wizard). */}
      <PublicEntryCTA />

      {/* 🗺️ Wave 4 · Public 24-month roadmap preview — 8 clickable
          checkpoints on a dark rail, deep-linking to /roadmap. */}
      <RoadmapPreview />

      {/* 🎯 Career Destinations — 4-col grid mapping placement reality */}
      <h2 id="employers-anchor" className="sr-only">Strategic Employer Partnerships</h2>
      <CareerDestinations />

      {/* 🏛️ Three Phases Breakdown — Triage → Sanctuary → Career */}
      <h2 id="alliances-anchor" className="sr-only">Non-Profit Alliance Integration</h2>
      <NonprofitsBreakdown />

      {/* 🤝 Employer Benefits — partner-facing tools that REPLACE the
          legacy bottom-ticker jargon */}
      <h2 id="ucms-anchor" className="sr-only">Personal Advancement and Career Training (PACT)</h2>
      <EmployerBenefits />

      {/* Partners + lead capture + footer — existing surfaces, restyled
          in the new palette */}
      <PartnersBand />
      {/* 🎠 PATCH-309 · Auto-scrolling gratitude ribbon for our
          sponsors/donors/supporters/residents/staff wall. Renders null
          when the CMS is empty so the landing stays clean until content
          exists. */}
      <SupportersCarousel />
      <InquiryForm />
      <Footer />
    </main>
  );
}


// ────────────────────────────────────────────────────────────────────
// 👷📝🏛️ Homepage Action Grid — Iter332 (2026-03-02)
// ────────────────────────────────────────────────────────────────────
// Expanded from a 2-card grid ("Estimate labor" + "Ask for a seat")
// into a symmetrical 3-card grid with a new "OUR FOUNDATION" card
// linking to the dedicated /four-pillars marketing landing.
function PublicEntryCTA() {
  return (
    <section
      className="bg-white py-12 md:py-16 border-y border-[#0A0612]/5"
      data-testid="public-entry-cta"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <a
            href="/labor"
            data-testid="landing-labor-cta"
            className="group rounded-2xl border-2 border-[#0A0612]/10 hover:border-[#C9A227] p-8 md:p-10 flex flex-col transition-all bg-[#FAFAF7] hover:shadow-lg hover:-translate-y-1"
          >
            <div className="h-12 w-12 rounded-full bg-[#0A0612] text-[#F4D88A] grid place-items-center mb-4">
              <Briefcase className="h-5 w-5" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mb-2">For Employers</div>
            <div className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0A0612] mb-2">
              Estimate your labor cost.
            </div>
            <div className="text-[#0A0612]/70 text-sm md:text-base leading-relaxed flex-1">
              Vetted, program-supported workers at 25–40% below market. Slide, calc, submit.
            </div>
            <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[#0A0612] group-hover:text-[#1B4332]">
              Open Calculator <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </a>

          {/* 🏛️ Iter332 · Four Pillars marketing bridge.
              Placed in the MIDDLE of the 3-card row so the "Our
              Foundation" story sits between the employer and resident
              CTAs — anchoring the pathway visually and thematically.
              Photographic backdrop (architectural stone pillars) with
              a dark tint keeps executive-warm text legible; card lifts
              on hover; image inside scales for a subtle cinematic feel.
              Photo: Unsplash `photo-1519681393784-d120267933ba` —
              modern columnar architecture at golden hour, SA-approved. */}
          <a
            href="/four-pillars"
            data-testid="landing-four-pillars-cta"
            className="group relative rounded-2xl overflow-hidden border-2 border-[#0A0612]/10 hover:border-[#C9A227] p-8 md:p-10 flex flex-col transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 min-h-[280px]"
          >
            {/* Photographic backdrop */}
            <div
              aria-hidden
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&q=80&w=1400')",
              }}
            />
            {/* Dark gradient overlay for text contrast (AA compliant) */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-br from-[#0A0612]/85 via-[#0A0612]/70 to-[#0A0612]/55 group-hover:from-[#0A0612]/80 group-hover:via-[#0A0612]/60 transition-colors"
            />
            {/* Content */}
            <div className="relative flex flex-col h-full">
              <div className="h-12 w-12 rounded-full bg-[#C9A227] text-[#0A0612] grid place-items-center mb-4 border-2 border-[#F4D88A]/50">
                <Columns3 className="h-5 w-5" />
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#F4D88A] mb-2">
                Our Foundation
              </div>
              <div className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#F5F1E8] mb-2 leading-tight">
                Discover the 4 Pillars of Transformation.
              </div>
              <div className="text-[#E8E5DA]/90 text-sm md:text-base leading-relaxed flex-1">
                Explore the Case Management, Life Skills, Christ-Centered
                Counseling, and Daily Check-In structure driving our
                24-month roadmap.
              </div>
              <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[#F4D88A] group-hover:text-white">
                Explore Our 4 Pillars <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </a>

          <a
            href="/intake"
            data-testid="landing-intake-cta"
            className="group rounded-2xl border-2 border-[#0A0612]/10 hover:border-[#1B4332] p-8 md:p-10 flex flex-col transition-all bg-[#FAFAF7] hover:shadow-lg hover:-translate-y-1"
          >
            <div className="h-12 w-12 rounded-full bg-[#1B4332] text-[#F4D88A] grid place-items-center mb-4">
              <HomeIcon className="h-5 w-5" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] mb-2">Need Support?</div>
            <div className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0A0612] mb-2">
              Ask for a seat at the table.
            </div>
            <div className="text-[#0A0612]/70 text-sm md:text-base leading-relaxed flex-1">
              4-minute confidential intake for housing + case management + workforce support.
            </div>
            <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-[#0A0612] group-hover:text-[#1B4332]">
              Start Intake <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
